import { Box, Portal, Stack, Tooltip } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import React, { useContext, useEffect, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';
import { selectAuthForNavigation, useAppDispatch, useAppSelector } from 'store/hooks';
import IconHandler from 'components/common/icon/IconHandler';
import styles from 'app/navbar/NavbarMenu.styles';
import getData, { mainMenuDataType, MainMenuItem, MainMenuItemsList, NavBarMenuComponentsRegistry } from './MenuConfig';
import IconMi from 'components/common/icon/IconMi';
import { useCheckUserAccess } from 'hooks/useCheckUserAccess';
import { customRequestAPI } from 'api/api';
import FormComponent from 'components/common/form/Form';
import { useMiNavigation } from 'hooks/useNavigation';
import RouteLink from 'components/common/route-link/RouteLink';
import { UserAuth } from 'store/auth';
import { useTheme } from '@mui/material/styles';

interface IMainMenuContext {
    openedMenu: string | null;
    setOpenedMenu: (value: string | null) => void;
    menuViewType: 'horizontal' | 'vertical';
    menuItemClick: (item: MainMenuItem, event: React.MouseEvent<HTMLElement>) => void;
}
export const MainMenuContext = React.createContext<IMainMenuContext>({
    openedMenu: null,
    setOpenedMenu: () => {},
    menuViewType: 'horizontal',
    menuItemClick: () => {},
});

interface SubMenuProps {
    items?: MainMenuItemsList;
    active: boolean;
    level?: number;
    subMenuActive: any;
    setSubMenuActive: any;
    rootItemKey?: string;
    holderItemEl: any;
    rootItemRef: any;
}

const MENU_WIDTH = 190;

function MenuBox({ rootItem }: { rootItem: MainMenuItem }) {
    const { openedMenu, setOpenedMenu, menuItemClick } = useContext(MainMenuContext);
    const [subMenuActive, setSubMenuActive] = React.useState<boolean>(false);
    const { t } = useBundleTranslation(['components/common/navbar/navbar_menu']);

    const userAuth: UserAuth = useAppSelector((state) => state.auth);

    const isActiveMenu = rootItem.key === openedMenu;
    const rootItemRef = useRef<HTMLDivElement>(null);

    const getUserItem = (username: string, isActive: boolean) => {
        const isNeedCut = username.length > 30;
        const tooltip = isNeedCut && !isActive ? username : '';
        const nick = isNeedCut ? username.slice(0, 30) + '...' : username;

        return (
            <Tooltip title={tooltip}>
                <Box component="span" className={'navbar-menu__root-item-label navbar-menu__root-item-label--username'}>
                    {nick}
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box
            key={rootItem.key}
            className={`navbar-menu__root-item-holder ${isActiveMenu ? 'navbar-menu__root-item-holder--active' : ''}`}
            onClick={(e) => {
                if (rootItem.children) {
                    setOpenedMenu(rootItem.key);
                } else {
                    menuItemClick(rootItem, e);
                }
            }}
            onMouseEnter={() => {
                if (openedMenu && !isActiveMenu && rootItem.children) setOpenedMenu(rootItem.key);
            }}
            onMouseLeave={() => {
                setSubMenuActive(false);
            }}
            ref={rootItemRef}
        >
            <Stack
                className={`navbar-menu__root-item`}
                direction="row"
                alignItems="center"
                component={!rootItem.href ? 'div' : RouteLink}
                to={rootItem.href ?? ''}
                reloadDocument={rootItem.href && rootItem.isExternal ? true : undefined}
            >
                <>
                    {rootItem.icon && (
                        <Tooltip
                            title={
                                rootItem.key == 'user' && userAuth.userInfo?.hijack_username
                                    ? `${t('logged_in_as')} ${rootItem.label}`
                                    : ''
                            }
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                className={`navbar-menu__root-item-icon ${rootItem.key == 'user' && rootItem.icon.type == 'upload' ? 'avatar-icon' : ''} ${rootItem.key == 'user' && userAuth.userInfo?.hijack_username ? 'anim-pulsing' : ''}`}
                            >
                                <IconHandler icon={rootItem.icon} />
                            </Stack>
                        </Tooltip>
                    )}
                    {rootItem.key == 'user' ? (
                        getUserItem(rootItem.label, isActiveMenu)
                    ) : (
                        <Box component="span" className={'navbar-menu__root-item-label'}>
                            {false === rootItem.translate ? rootItem.label : t(rootItem.label)}
                        </Box>
                    )}
                </>
            </Stack>
            {rootItemRef.current && rootItem.children && rootItem.children.length > 0 && (
                <SubMenu
                    rootItemRef={rootItemRef}
                    holderItemEl={rootItemRef.current}
                    rootItemKey={rootItem.key}
                    items={rootItem.children}
                    active={isActiveMenu}
                    subMenuActive={subMenuActive}
                    setSubMenuActive={setSubMenuActive}
                />
            )}
        </Box>
    );
}

function SubMenu(props: SubMenuProps) {
    const {
        items = [],
        active,
        level = 1,
        setSubMenuActive,
        subMenuActive = null,
        rootItemKey,
        holderItemEl,
        rootItemRef,
    } = props;
    const { openedMenu, menuItemClick, menuViewType } = useContext(MainMenuContext);
    const { t } = useBundleTranslation(['components/common/navbar/navbar_menu']);
    const [hoveredItem, setHoveredItem] = useState<null | string>(null);

    const menuItemRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const submenuHolderRef = useRef<HTMLDivElement>(null);
    const appTheme = useTheme();

    const [subMenuSettings, setSubMenuSettings] = useState<any>({
        maxHeight: 'none',
        position: {},
    });

    const adjustSubMenuSettings = () => {
        let position: any = {};
        let newMaxHeight = 'none';
        const verticalIndentToTheEdge = 20;
        const newMenuMaxHeight = 340;

        if (level == 1) {
            //root submenu
            if (menuViewType == 'horizontal') {
                newMaxHeight = `calc(100vh - ${holderItemEl.getBoundingClientRect().bottom + verticalIndentToTheEdge}px)`;
            } else if (menuViewType == 'vertical') {
                newMaxHeight =
                    holderItemEl.getBoundingClientRect().bottom -
                    verticalIndentToTheEdge -
                    appTheme.size.navbarHeight +
                    'px';
            }
        } else {
            if (menuViewType == 'horizontal') {
                if (holderItemEl.getBoundingClientRect().left + 2 * MENU_WIDTH + 12 > window.innerWidth) {
                    position.right = MENU_WIDTH;
                } else {
                    position.left = '100%';
                }

                //top or bottom check by content
                const isNotEnoughSpaceToFullDisplay =
                    submenuHolderRef.current &&
                    submenuHolderRef.current.scrollHeight >
                        window.innerHeight - holderItemEl.getBoundingClientRect().top - verticalIndentToTheEdge;

                const availableTopSpace =
                    holderItemEl.getBoundingClientRect().bottom - appTheme.size.navbarHeight - verticalIndentToTheEdge;
                const availableBottomSpace =
                    window.innerHeight - holderItemEl.getBoundingClientRect().top - verticalIndentToTheEdge;

                if (isNotEnoughSpaceToFullDisplay && availableTopSpace > availableBottomSpace) {
                    position.bottom =
                        rootItemRef.current.getBoundingClientRect().bottom -
                        holderItemEl.getBoundingClientRect().bottom;
                    newMaxHeight =
                        (rootItemKey == 'new' && availableTopSpace > newMenuMaxHeight
                            ? newMenuMaxHeight
                            : availableTopSpace) + 'px';
                } else {
                    position.top =
                        holderItemEl.getBoundingClientRect().top - rootItemRef.current.getBoundingClientRect().top;
                    newMaxHeight =
                        (rootItemKey == 'new' && availableBottomSpace > newMenuMaxHeight
                            ? newMenuMaxHeight
                            : availableBottomSpace) + 'px';
                }
            } else if (menuViewType == 'vertical') {
                position.left = rootItemRef.current.getBoundingClientRect().width + MENU_WIDTH;

                //top or bottom check by content
                const isNotEnoughSpaceToFullDisplay =
                    submenuHolderRef.current &&
                    submenuHolderRef.current.scrollHeight >
                        holderItemEl.getBoundingClientRect().bottom -
                            appTheme.size.navbarHeight -
                            verticalIndentToTheEdge;

                const availableTopSpace =
                    holderItemEl.getBoundingClientRect().bottom - appTheme.size.navbarHeight - verticalIndentToTheEdge;
                const availableBottomSpace =
                    window.innerHeight - holderItemEl.getBoundingClientRect().top - verticalIndentToTheEdge;

                if (isNotEnoughSpaceToFullDisplay && availableBottomSpace > availableTopSpace) {
                    position.top =
                        holderItemEl.getBoundingClientRect().top - rootItemRef.current.getBoundingClientRect().top;
                    newMaxHeight =
                        (rootItemKey == 'new' && availableBottomSpace > newMenuMaxHeight
                            ? newMenuMaxHeight
                            : availableBottomSpace) + 'px';
                } else {
                    position.bottom =
                        rootItemRef.current.getBoundingClientRect().bottom -
                        holderItemEl.getBoundingClientRect().bottom;
                    newMaxHeight =
                        (rootItemKey == 'new' && availableTopSpace > newMenuMaxHeight
                            ? newMenuMaxHeight
                            : availableTopSpace) + 'px';
                }
            }
        }

        setSubMenuSettings({
            maxHeight: newMaxHeight,
            position: position,
        });
    };

    useEffect(() => {
        let scrollContainer: any;
        if (holderItemEl) {
            scrollContainer = holderItemEl.closest('.navbar-submenu');
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', adjustSubMenuSettings);
            }
        }

        window.addEventListener('resize', adjustSubMenuSettings);

        return () => {
            window.removeEventListener('resize', adjustSubMenuSettings);
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', adjustSubMenuSettings);
            }
        };
    }, []);

    useEffect(() => {
        adjustSubMenuSettings();
    }, [holderItemEl, active, submenuHolderRef.current, rootItemRef.current]);

    useEffect(() => {
        setHoveredItem(null);
    }, [openedMenu]);

    useEffect(() => {
        let delayTimerId: any;
        if (!subMenuActive) {
            delayTimerId = setTimeout(function () {
                setHoveredItem(null);
            }, 400);
        }

        return () => clearTimeout(delayTimerId);
    }, [subMenuActive]);

    const onHoverItem = (item: MainMenuItem, event: React.MouseEvent<HTMLElement>, level: number, index: number) => {
        //we can replace with key, but we need to set keys for all items
        setHoveredItem(`${level}_${index}`);

        setSubMenuActive(true);
    };

    const isHoveredIndex = hoveredItem ? Number(hoveredItem.split('_')[1]) : null;

    return (
        <Portal container={rootItemRef.current}>
            <Box
                sx={{ ...styles.submenuPopper, ...subMenuSettings.position }}
                className={`navbar-submenu-popper navbar-submenu-popper--level-${level == 1 ? 'root' : 'sub'} navbar-submenu-popper--view-${menuViewType} ${active ? 'navbar-submenu-popper--active' : ''}`}
            >
                <Box className="navbar-submenu-popper__container">
                    <Stack
                        className={`navbar-menu__submenu navbar-submenu navbar-submenu--level-${level}`}
                        sx={{
                            ...styles.submenuItemHolder,
                            width: MENU_WIDTH,
                            maxHeight: subMenuSettings.maxHeight,
                            overflow: 'auto',
                        }}
                        ref={submenuHolderRef}
                    >
                        {items.map((item, index) => {
                            if (item.divider) {
                                return <Box className={'navbar-submenu__divider'} key={index} />;
                            }

                            if (item.component) {
                                let props = {};
                                return React.createElement(NavBarMenuComponentsRegistry[item.component], {
                                    ...props,
                                    key: index,
                                });
                            }

                            return (
                                <Stack
                                    key={item.label}
                                    component={'div'}
                                    className={`navbar-submenu__item-holder ${
                                        isHoveredIndex === index ? 'navbar-submenu__item-holder--hovered' : ''
                                    }`}
                                    onClick={(e: any) => {
                                        menuItemClick(item, e);
                                    }}
                                    onMouseEnter={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                                        onHoverItem(item, event, level, index);
                                    }}
                                    ref={(el) => (menuItemRef.current[`${level}_${index}`] = el as any)}
                                >
                                    <Stack
                                        data-test={item?.key ?? undefined}
                                        component={!item.href ? 'div' : RouteLink}
                                        to={item.href ?? ''}
                                        reloadDocument={item.href && item.isExternal ? true : undefined}
                                        target={item.newTab ? '_blank' : undefined}
                                        rel="noopener noreferrer"
                                        className={`navbar-submenu__item`}
                                    >
                                        {item.icon && (
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="center"
                                                className={'navbar-submenu__item-icon'}
                                            >
                                                <IconHandler
                                                    icon={item.icon}
                                                    sx={{ fontSize: item.icon?.type === 'fa' ? 14 : 16 }}
                                                />
                                            </Stack>
                                        )}
                                        <Box className="navbar-submenu__item-label" component="span">
                                            {false === item.translate ? item.label : t(item.label)}
                                        </Box>
                                        {item.children && item.key !== 'about' && (
                                            <Box className="navbar-submenu__item-children-ico">
                                                <IconMi icon={'chevron-right'} />
                                            </Box>
                                        )}
                                    </Stack>
                                    {menuItemRef.current[`${level}_${index}`] &&
                                        item.children &&
                                        item.children.length > 0 && (
                                            <SubMenu
                                                rootItemRef={rootItemRef}
                                                active={isHoveredIndex === index}
                                                holderItemEl={menuItemRef.current[`${level}_${index}`]}
                                                rootItemKey={rootItemKey}
                                                items={item.children}
                                                level={level + 1}
                                                setSubMenuActive={setSubMenuActive}
                                                subMenuActive={subMenuActive}
                                            />
                                        )}
                                </Stack>
                            );
                        })}
                    </Stack>
                </Box>
            </Box>
        </Portal>
    );
}

export default function NavbarMenu(props: { view?: 'horizontal' | 'vertical'; dataType: mainMenuDataType }) {
    const { view = 'horizontal', dataType = 'all' } = props;
    const { t } = useBundleTranslation(['components/common/navbar/navbar_menu']);
    const [openedMenu, setOpenedMenu] = React.useState<null | string>(null);
    const [newModalData, setNewModalData] = useState<any>();

    const state = useAppSelector(selectAuthForNavigation);

    const dispatch = useAppDispatch();
    const navigate = useMiNavigation();
    const checkCondition = useCheckUserAccess();
    const menu: MainMenuItemsList = getData(t, state, checkCondition, dataType);

    const createNewFunc = (url: string, popupSettings?: any) => {
        setNewModalData('');

        if (popupSettings) {
            setNewModalData({ ...popupSettings, url });
        } else {
            customRequestAPI
                .post(url, {})
                .then((response: any) => {
                    if ('ERROR' === response.data.status) alert(response.data.message);
                    else if (response.data.redirectUrl) setTimeout(() => navigate(response.data.redirectUrl), 100);
                })
                .catch(() => {});
        }
    };
    const menuItemClick = (item: MainMenuItem, event: React.MouseEvent<HTMLElement>) => {
        if (!item.href && !item.action) return;

        event.stopPropagation();

        if (item.action) {
            item.action({ dispatch, createNewFunc });
        }

        setOpenedMenu(null);
    };

    useEffect(() => {
        if (!openedMenu) {
            if (dataType == 'leftbar') document.body.classList.remove('navbar-menu--state-opened');
        } else {
            if (dataType == 'leftbar') document.body.classList.add('navbar-menu--state-opened');
        }
    }, [openedMenu]);
    return (
        <MainMenuContext.Provider value={{ openedMenu, setOpenedMenu, menuViewType: view, menuItemClick }}>
            <ClickAwayListener
                onClickAway={() => {
                    setOpenedMenu(null);
                }}
            >
                <Stack
                    direction={view == 'horizontal' ? 'row' : 'column'}
                    spacing={view == 'horizontal' ? 0.5 : 0.25}
                    sx={styles.navBarMenu}
                    className={`navbar-menu navbar-menu--${view} navbar-menu--${openedMenu ? 'opened' : 'closed'}`}
                >
                    {menu.map((rootItem, index) => {
                        //@ts-ignore
                        if (rootItem.component) {
                            let props = {};
                            //@ts-ignore
                            if (rootItem.component == 'MenuGlobalDocuments') {
                                props = {
                                    onChangeState: (opened: any) => {
                                        if (opened) setOpenedMenu(null);
                                    },
                                };
                            }
                            //@ts-ignore
                            return React.createElement(NavBarMenuComponentsRegistry[rootItem.component], {
                                ...props,
                                key: index,
                            });
                        }
                        if (rootItem.divider) {
                            return <Box className={'navbar-menu__item-divider'} key={index} />;
                        }
                        return <MenuBox key={index} rootItem={rootItem} />;
                    })}
                    {newModalData && (
                        <FormComponent
                            isPopup
                            popupType={'new'}
                            pk={newModalData.url}
                            uid={newModalData.uid}
                            settingsUrl={newModalData.url}
                            onHide={() => {
                                setNewModalData('');
                            }}
                            popupSettings={{}}
                            afterSave={(response: any) => {
                                if (newModalData.redirectAfter) {
                                    if (response.data && response.data.redirectUrl) {
                                        setTimeout(() => navigate(response.data.redirectUrl), 100);
                                    }
                                }
                            }}
                        />
                    )}
                </Stack>
            </ClickAwayListener>
        </MainMenuContext.Provider>
    );
}
